import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// --- Types ---
interface SessionDividerProps {
  sessionDate: string;
  showDateText?: boolean;
  isCurrentSession?: boolean;
}

// --- Styled Components ---
const DividerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: theme.spacing(2, 0),
  position: 'relative',
}));

const DividerLine = styled(Box)(() => ({
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 0,
  height: '1px',
  backgroundColor: '#E4E7EC',
  zIndex: 0,
}));

const DividerContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  backgroundColor: 'white',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '12px',
  border: '1px solid #E4E7EC',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.04)',
}));

const CurrentSessionMarker = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  backgroundColor: '#3660F4',
  color: 'white',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '12px',
  boxShadow: '0px 2px 4px rgba(54, 96, 244, 0.15)',
}));

const DateText = styled(Typography)(() => ({
  fontSize: '12px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  color: '#111827',
  lineHeight: 1,
}));

const CurrentSessionText = styled(Typography)(() => ({
  fontSize: '12px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  color: 'white',
  lineHeight: 1,
}));

// --- Utility Functions ---

/**
 * Format a timestamp into a readable date string in French
 */
const formatSessionDate = (dateString: string): string => {
  if (!dateString || typeof dateString !== "string") {
    return "Date inconnue";
  }
  const date = new Date(dateString);
  const ts = date.getTime();
  if (Number.isNaN(ts)) {
    return "Date inconnue";
  }
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sessionDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - sessionDay.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return "Aujourd'hui";
  } else if (diffDays === 1) {
    return "Hier";
  } else if (diffDays < 7) {
    return `Il y a ${diffDays} jours`;
  } else {
    // Format: "Jeudi 5 février"
    return date.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }
};

// --- Component ---
export const SessionDivider: React.FC<SessionDividerProps> = ({
  sessionDate,
  isCurrentSession = false,
  showDateText = true,
}) => {
  const formattedDate = isCurrentSession ? "Aujourd'hui" : formatSessionDate(sessionDate);

  if (isCurrentSession) {
    return (
      <DividerContainer id="divider-current-session">
        <DividerLine />
        {showDateText && (
        <CurrentSessionMarker>
            <CurrentSessionText>{formattedDate}</CurrentSessionText>
          </CurrentSessionMarker>
        )}
      </DividerContainer>
    );
  }

  return (
    <DividerContainer>
      <DividerLine />
      {showDateText && (
      <DividerContent>
        <DateText>{formattedDate}</DateText>
      </DividerContent>
      )}
    </DividerContainer>
  );
};

export default SessionDivider;
